import './App.css';
import { useState, useEffect } from 'react';
import logo from './logo.png';
import northcrestLogo from './northcrest.png';
import { PiRocketLaunchLight } from "react-icons/pi";
import { HiOutlinePencilAlt, HiOutlineDesktopComputer, HiOutlineShieldCheck } from "react-icons/hi";
import { FaChevronLeft, FaChevronRight, FaPause, FaPlay } from "react-icons/fa";
import { HiDotsVertical } from "react-icons/hi";

function App() {
  const [openFaq, setOpenFaq] = useState(null);
  const [darkMode, setDarkMode] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark-mode');
    } else {
      document.documentElement.classList.remove('dark-mode');
    }
  }, [darkMode]);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    const animateElements = document.querySelectorAll('.animate-on-scroll');
    animateElements.forEach((el) => observer.observe(el));

    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const navbar = document.querySelector('.navbar');
          if (window.scrollY > 50) {
            navbar?.classList.add('navbar-scrolled');
          } else {
            navbar?.classList.remove('navbar-scrolled');
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      animateElements.forEach((el) => observer.unobserve(el));
      window.removeEventListener('scroll', handleScroll, { passive: true });
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const navMenu = document.querySelector('.nav-menu');
      const menuToggle = document.querySelector('.mobile-menu-toggle');
      if (mobileMenuOpen && navMenu && !navMenu.contains(event.target) && !menuToggle?.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // Custom smooth scroll with eased motion and slightly longer duration
  const smoothScrollTo = (targetY, duration = 900) => {
    const startY = window.scrollY || window.pageYOffset;
    const distance = targetY - startY;
    const startTime = performance.now();

    const easeInOutQuad = (t) =>
      t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

    const step = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeInOutQuad(progress);

      window.scrollTo(0, startY + distance * eased);

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  };

  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing');
    if (pricingSection) {
      const offsetTop = pricingSection.offsetTop - 80;
      smoothScrollTo(offsetTop, 1100);
    }
  };

  const handleNavClick = (e, targetId) => {
    e.preventDefault();
    const targetSection = document.getElementById(targetId);
    if (targetSection) {
      const offsetTop = targetSection.offsetTop - 80;
      smoothScrollTo(offsetTop, 1000);
    }
  };

  const testimonials = [
    {
      quote: "Northcrest delivered exactly what we needed — fast, modern, and built for growth.",
      author: "Olivia Park",
      role: "Founder, Helio Labs"
    },
    {
      quote: "Exceptional clarity and communication. Their team made a complex project feel effortless.",
      author: "Daniel Brooks",
      role: "Operations Manager, Vertex Automation"
    },
    {
      quote: "Our brand finally looks world-class online. Northcrest understood our vision better than we did.",
      author: "Priya Menon",
      role: "CEO, Luminate Systems"
    },
    {
      quote: "From strategy to launch, everything was handled with precision. Truly impressive work.",
      author: "Liam Carter",
      role: "CPO, QuantumWorks"
    },
    {
      quote: "Northcrest combined design, performance, and patience in every step. Couldn’t have asked for better partners.",
      author: "Chloe Ramirez",
      role: "Marketing Lead, AeronEdge"
    },
    {
      quote: "Their ability to turn feedback into flawless execution is rare. The project exceeded expectations.",
      author: "Jonathan Meyer",
      role: "Director of Product, Flux Industries"
    },
    {
      quote: "Professional, responsive, and highly detail-oriented. The new site boosted our credibility instantly.",
      author: "Neha Kapoor",
      role: "Managing Director, OptiFlow Technologies"
    },
    {
      quote: "They delivered a scalable, polished site that our clients love. Communication throughout was excellent.",
      author: "Michael Harris",
      role: "CTO, TitanCore Engineering"
    },
    {
      quote: "Northcrest brought clarity to our digital presence. The team was patient, creative, and incredibly reliable.",
      author: "Sofia Bennett",
      role: "VP Strategy, Horizon Tech Partners"
    },
    {
      quote: "Every milestone was delivered on time with zero surprises. Their process is as good as their work.",
      author: "Aiden Scott",
      role: "COO, MotionGrid Robotics"
    },
    {
      quote: "Immediate performance gains — the Northcrest team was proactive, patient, and great to work with.",
      author: "Ethan Garcia",
      role: "CTO, Nimbus Robotics"
    },
    {
      quote: "Clear communication, smooth delivery, and visible results within weeks.",
      author: "Noah Romero",
      role: "Product Lead, NovaForm"
    },
    {
      quote: "Felt like an internal team — responsive, detail-focused, and execution-driven.",
      author: "Amira Chen",
      role: "VP Marketing, Apex Dynamics"
    },
    {
      quote: "Seamless end-to-end delivery. Reliable, professional, and on-time.",
      author: "Amira Nguyen",
      role: "VP Marketing, Brightline Systems"
    },
    {
      quote: "Great communication, steady guidance, and measurable improvement in conversions.",
      author: "Amira Nguyen",
      role: "Operations Lead, Nimbus Robotics"
    },
    {
      quote: "Clean design, strong messaging, and a patient team that really listens.",
      author: "Marcus Nguyen",
      role: "VP Marketing, Volt Manufacturing"
    },
    {
      quote: "Highly responsive, clear, and committed — every request handled with care.",
      author: "Sara Lewis",
      role: "CTO, Apex Dynamics"
    },
    {
      quote: "Strategic, supportive, and collaborative — Northcrest elevated our digital presence.",
      author: "Sara Williams",
      role: "Marketing Director, TerraWave"
    }
  ];

  const faqItems = [
    {
      id: 1,
      question: "Do you handle everything from branding to website and e-commerce development?",
      answer: "Yes, we provide comprehensive digital solutions including branding, website design, development, and e-commerce integration. Our team manages the entire process from concept to launch."
    },
    {
      id: 2,
      question: "What's your process for delivering a project?",
      answer: "Our process follows four key stages: Discovery (understanding your business and goals), Design (creating custom designs), Build (developing with clean code), and Launch & Care (deployment and ongoing support)."
    },
    {
      id: 3,
      question: "How long does a typical project take?",
      answer: "Project timelines vary based on scope. Basic websites typically take 2-4 weeks, standard websites 4-8 weeks, and premium websites 8-12 weeks. We'll provide a detailed timeline during the discovery phase."
    },
    {
      id: 4,
      question: "Do you provide marketing services as well?",
      answer: "While our primary focus is website design and development, we can provide basic SEO optimization and digital strategy guidance. For comprehensive marketing services, we can recommend trusted partners."
    },
    {
      id: 5,
      question: "Do you build MVPs or just full-scale products?",
      answer: "We work with businesses at all stages. Whether you need an MVP to test your concept or a full-scale website, we can accommodate your needs and scale accordingly."
    },
    {
      id: 6,
      question: "Do you provide post-launch support?",
      answer: "Yes, we offer ongoing maintenance and support packages. Our Care plans include updates, backups, security monitoring, and technical support to keep your website running smoothly."
    },
    {
      id: 7,
      question: "What if I need ongoing updates and changes?",
      answer: "We offer flexible maintenance plans that include regular updates, content changes, and technical support. You can choose a plan that fits your needs, from basic maintenance to comprehensive care packages."
    },
    {
      id: 8,
      question: "Do you work with startups or only established businesses?",
      answer: "We work with businesses of all sizes, from startups to established companies. Our flexible pricing and packages are designed to accommodate different business needs and budgets."
    },
    {
      id: 9,
      question: "What platforms do you build on?",
      answer: "We build on modern, reliable platforms including React, Next.js, and other contemporary web technologies. We choose the best platform based on your specific needs and requirements."
    }
  ];

  const toggleFaq = (id) => {
    setOpenFaq(openFaq === id ? null : id);
  };

  return (
    <div className="App">
      <nav className="navbar">
        <div className="navbar-container">
          <div className="logo-section">
            <button 
              className="logo-button" 
              onClick={(e) => handleNavClick(e, 'home')}
            >
              <div className="logo-text">
                <div className="project-label">Project</div>
                <div className="brand-name">Northcrest®</div>
              </div>
            </button>
          </div>
          <button 
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <HiDotsVertical />
          </button>
          <div className={`nav-menu ${mobileMenuOpen ? 'nav-menu-open' : ''}`}>
            <a href="#home" className="nav-link" onClick={(e) => { handleNavClick(e, 'home'); setMobileMenuOpen(false); }}>Home</a>
            <a href="#services" className="nav-link" onClick={(e) => { handleNavClick(e, 'services'); setMobileMenuOpen(false); }}>Services</a>
            <a href="#pricing" className="nav-link" onClick={(e) => { handleNavClick(e, 'pricing'); setMobileMenuOpen(false); }}>Plans</a>
            <a 
              href="https://calendly.com/abdulshukkurhere/30min" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="nav-cta"
              onClick={() => setMobileMenuOpen(false)}
            >
              Book a call
            </a>
          </div>
        </div>
      </nav>

      <section id="home" className="hero-section">
        <div className="hero-container">
          <div className="hero-title-wrapper">
            <h1 className="hero-title animate-on-scroll">
              We don't just build websites.<br />
              We build revenue engines.
            </h1>
          </div>
          <p className="hero-subtitle animate-on-scroll">Your website should do more than look good - it should grow your <strong>business</strong>. We design <strong>digital platform</strong> that converts trust into Customer and clicks into revenue.</p>
          <div className="button-group animate-on-scroll">
            <a 
              href="https://calendly.com/abdulshukkurhere/30min" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-primary"
            >
              Book a call
            </a>
          </div>
        </div>
      </section>

      <section className="process-section">
        <div className="process-container">
          <div className="process-step animate-on-scroll">
            <div className="step-icon">
              <PiRocketLaunchLight />
            </div>
            <h3 className="step-title">Discovery</h3>
            <p className="step-description">We learn about your business, goals, and target audience to create a strategic foundation.</p>
          </div>
          <div className="process-step animate-on-scroll">
            <div className="step-icon">
              <HiOutlinePencilAlt />
            </div>
            <h3 className="step-title">Design</h3>
            <p className="step-description">Our team creates custom designs that reflect your brand and engage your visitors.</p>
          </div>
          <div className="process-step animate-on-scroll">
            <div className="step-icon">
              <HiOutlineDesktopComputer />
            </div>
            <h3 className="step-title">Build</h3>
            <p className="step-description">We develop your website with clean code, ensuring fast performance and reliability.</p>
          </div>
          <div className="process-step animate-on-scroll">
            <div className="step-icon">
              <HiOutlineShieldCheck />
            </div>
            <h3 className="step-title">Launch & Care</h3>
            <p className="step-description">We launch your site and provide ongoing maintenance to keep it running smoothly.</p>
          </div>
        </div>
      </section>

      <section id="services" className="service-cards-section">
        <div className="service-cards-container">
          <div className="services-header animate-on-scroll">
            <h2 className="services-title">Services</h2>
            <p className="services-subtitle">Northcrest combines brand clarity, modern design, and performance-led development to create websites that build trust and drive business outcomes.</p>
          </div>
          <div className="service-cards-grid">
            <div className="service-card animate-on-scroll">
            <div className="card-label">Websites</div>
            <h3 className="card-title">Website Design</h3>
            <p className="card-description">
            We create modern, fast, and high-impact websites that instantly elevate your brand. Every site is custom-built, mobile-responsive, and optimized for performance and conversions — helping you look professional and attract more customers.            </p>
          </div>
          <div className="service-card animate-on-scroll">
            <div className="card-label">Portfolios</div>
            <h3 className="card-title">Portfolio Development</h3>
            <p className="card-description">
            Turn your work into a compelling digital showcase. We design visually engaging portfolios that highlight your expertise, build credibility, and make it easy for clients to explore your capabilities.            </p>
          </div>
          <div className="service-card animate-on-scroll">
            <div className="card-label">Care</div>
            <h3 className="card-title">Maintenance & Support</h3>
            <p className="card-description">
            Keep your website secure, updated, and running smoothly. We handle backups, updates, security monitoring, and technical support so your site stays optimized and worry-free.
            </p>
          </div>
          </div>
        </div>
      </section>

      <section className="highlight-section">
        <div className="service-cards-container">
          <div className="service-cards-grid">
            <div className="service-card animate-on-scroll">
              <div className="card-label">Corporate</div>
              <h3 className="card-title">Corporate Website Design</h3>
              <p className="card-description">
              Build trust and authority with a professional online presence. We design corporate websites that reflect your expertise, strengthen credibility, and effectively communicate your value to clients and partners.
              </p>
            </div>
            <div className="service-card animate-on-scroll">
              <div className="card-label">Strategy</div>
              <h3 className="card-title">Digital Strategy & UX</h3>
              <p className="card-description">
              Turn your website into a strategic asset. We craft user-focused experiences and digital strategies that clearly communicate your value, boost engagement, and drive conversions.
              </p>
            </div>
            <div className="service-card animate-on-scroll">
              <div className="card-label">Development</div>
              <h3 className="card-title">Custom Web Development</h3>
              <p className="card-description">
              Get a fast, scalable, future-ready website. We build custom solutions using modern technologies, ensuring your site is secure, high-performing, and easy to maintain.              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="portfolio" className="portfolio-section">
        <div className="portfolio-container">
          <h2 className="portfolio-title animate-on-scroll">Testimonials</h2>
          <p className="portfolio-subtitle animate-on-scroll">
            A sampling of impactful digital projects crafted for engineering and industrial visionaries. Each project elevates client credibility with modern design and seamless experience.
          </p>
          <div className="testimonial-carousel">
            <div className="testimonial-row">
              <div className="testimonial-card-wrapper">
                <div className="testimonial-cards-container testimonial-cards-container-top">
                  {[...testimonials, ...testimonials].map((testimonial, i) => (
                    <div key={`testimonial-${i}`} className="testimonial-card">
                      <p className="testimonial-quote">“{testimonial.quote}”</p>
                      <div className="testimonial-meta">
                        <div className="testimonial-author">{testimonial.author}</div>
                        <div className="testimonial-role">{testimonial.role}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="pricing" className="pricing-section">
        <div className="pricing-container">
          <h2 className="pricing-title animate-on-scroll">Plans</h2>
          <p className="pricing-subtitle animate-on-scroll">Flexible options for different business needs.</p>
          <div className="pricing-grid">
            <div className="pricing-card animate-on-scroll">
              <h3 className="package-name">Starter</h3>
              <div className="package-price">$500</div>
              <ul className="features-list">
                <li>1–3 pages</li>
                <li>Basic layout</li>
                <li>Simple contact form</li>
                <li>Responsive design</li>
              </ul>
            </div>
            <div className="pricing-card animate-on-scroll">
              <h3 className="package-name">Professional</h3>
              <div className="package-price">$1200</div>
              <ul className="features-list">
                <li>4-6 pages</li>
                <li>Custom layout and animations</li>
                <li>SEO optimization</li>
                <li>Advanced responsive design</li>
              </ul>
            </div>
            <div className="pricing-card animate-on-scroll">
              <h3 className="package-name">Elite</h3>
              <div className="package-price">$2000</div>
              <ul className="features-list">
                <li>Full corporate site</li>
                <li>CMS,analytics,scalability</li>
                <li>Advanced functionality</li>
                <li>Performance optimization</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      <section className="faq-section">
        <div className="faq-container">
          <h2 className="faq-title animate-on-scroll">Questions? We got answers.</h2>
          <div className="faq-list">
            {faqItems.map((item) => (
              <div key={item.id} className="faq-item">
                <button 
                  className="faq-question"
                  onClick={() => toggleFaq(item.id)}
                  aria-expanded={openFaq === item.id}
                >
                  <span>{item.question}</span>
                  <span className="faq-icon">{openFaq === item.id ? '−' : '+'}</span>
                </button>
                {openFaq === item.id && (
                  <div className="faq-answer">
                    <p>{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="about-section">
        <div className="about-container">
          <div className="about-brand">
            <div className="about-project-label">Project</div>
            <div className="about-brand-name">Northcrest®</div>
          </div>
        </div>
      </section>

    </div>
  );
}

export default App;
